import { createAsyncThunk } from '@reduxjs/toolkit';
import { metricsTypes } from 'components/Reporting/reportingConstants';
import { THOUSAND, SEC_PER_HR, HRS_PER_DAY, NINETY, THIRTY } from 'appConstants';

import {
  startReportGeneration as startReportGenerationApi,
  metrics as metricsApi,
  campaigns as campaignsApi,
  users as usersApi,
  getReportsUniqueMessageSent,
  getScheduledMessagesSearch as getScheduledMessagesSearchApi,
  getUserData,
  getSequenceCandidates,
  sequence as getSequenceMetricsApi,
} from 'util/Api/reporting';
import { sequences as sequencesApi } from 'util/Api/customer';

export const startReportGeneration = createAsyncThunk(
  'reporting/startReportGeneration',
  async ({ params, dateRange }, { rejectWithValue }) => {
    try {
      return await startReportGenerationApi({
        params,
        dateRange,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getAggregateMetrics = createAsyncThunk(
  'reporting/getAggregateMetrics',
  async ({ dateRange, channel }, { rejectWithValue }) => {
    try {
      return await metricsApi({
        metricsType: metricsTypes.CUSTOMER_METRICS,
        dateRange,
        channel,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getAggregateCampaignsMetrics = createAsyncThunk(
  'reporting/getAggregateCampaignsMetrics',
  async ({ dateRange, channel }, { rejectWithValue }) => {
    try {
      return await metricsApi({
        metricsType: metricsTypes.CAMPAIGN_METRICS,
        dateRange,
        channel,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getReportingSequence = createAsyncThunk(
  'reporting/getReportingSequence',
  async ({ filter }, { rejectWithValue }) => {
    try {
      return await sequencesApi({ filter });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getCampaignBreakdownMetrics = createAsyncThunk(
  'reporting/getCampaignBreakdownMetrics',
  async ({ dateRange, pageNumber, itemsPerPage, sort, filter }, { rejectWithValue }) => {
    try {
      return await campaignsApi({
        dateRange,
        pageNumber,
        itemsPerPage,
        sort,
        filter,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getUserBreakdownMetrics = createAsyncThunk(
  'reporting/getUserBreakdownMetrics',
  async ({ dateRange, pageNumber, itemsPerPage, sort, filter }, { rejectWithValue }) => {
    try {
      return await usersApi({
        dateRange,
        pageNumber,
        itemsPerPage,
        sort,
        filter,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const fetchMetricsApi = async ({ dateRange }, { rejectWithValue }) => {
  const diffInTime = dateRange.end.getTime() - dateRange.start.getTime();
  const diffInDays = diffInTime / (THOUSAND * SEC_PER_HR * HRS_PER_DAY);
  let timeframe = '';

  if (Math.round(diffInDays) <= THIRTY) {
    timeframe = 'day';
  } else if (Math.round(diffInDays) <= NINETY) {
    timeframe = 'week';
  } else {
    timeframe = 'month';
  }

  try {
    return await metricsApi({
      metricsType: metricsTypes.CUSTOMER_METRICS,
      dateRange,
      aggregationTimeframe: timeframe,
    });
  } catch (err) {
    return rejectWithValue(err);
  }
};

export const getUserBasicStatsAggregatedMetrics = createAsyncThunk(
  'reporting/getUserBasicStatsAggregatedMetrics',
  fetchMetricsApi,
);

export const getAnnualReportAggregatedMetrics = createAsyncThunk(
  'reporting/getAnnualReportAggregatedMetrics',
  fetchMetricsApi,
);

export const uniqMessageReceive = createAsyncThunk(
  'reporting/uniqueMessageGeneration',
  async ({ dateRange }, { rejectWithValue }) => {
    try {
      return await getReportsUniqueMessageSent({
        dateRange,
        aggregationTimeframe: 'week',
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const userMessageData = createAsyncThunk(
  'reporting/userMessageDataGeneration',
  async ({ dateRange, userId }, { rejectWithValue }) => {
    try {
      return await getUserData({
        dateRange,
        aggregationTimeframe: 'week',
        userId,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getScheduledMessagesSearch = createAsyncThunk(
  'reporting/getScheduledMessageSearch',
  async ({ userId, itemsPerPage, page }, { rejectWithValue }) => {
    try {
      return await getScheduledMessagesSearchApi({
        userId,
        itemsPerPage,
        page,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getSequenceMetrics = createAsyncThunk(
  'reporting/getSequenceMetrics',
  async ({ dateRange, sequenceId }, { rejectWithValue }) => {
    try {
      return await getSequenceMetricsApi({
        dateRange,
        sequenceId,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getSequenceCandidate = createAsyncThunk(
  'reporting/getSequenceCandidate',
  async ({ dateRange, sequenceId }, { rejectWithValue }) => {
    const diffInTime = dateRange.end.getTime() - dateRange.start.getTime();
    const diffInDays = diffInTime / (THOUSAND * SEC_PER_HR * HRS_PER_DAY);
    let aggregationTimeframe = '';

    if (Math.round(diffInDays) <= THIRTY) {
      aggregationTimeframe = 'day';
    } else if (Math.round(diffInDays) <= NINETY) {
      aggregationTimeframe = 'week';
    } else {
      aggregationTimeframe = 'month';
    }

    try {
      return await getSequenceCandidates({
        dateRange,
        aggregationTimeframe,
        sequenceId,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
